<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    :no-close-on-esc="isSubmitting"
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            isAdd
              ? `${$t("actions.add")} ${$tc("schedule_alarm.title", 1)}`
              : `${$t("actions.edit")} ${$tc("schedule_alarm.title", 1)}`
          }}
        </h5>

        <feather-icon
          class="ml-1"
          :class="isSubmitting ? 'text-muted' : 'cursor-pointer'"
          icon="XIcon"
          size="16"
          @click="!isSubmitting && hide()"
        />
      </div>

      <div class="p-1">
        <!-- BODY -->
        <validation-observer
          ref="refFormObserver"
        >
          <!-- Name -->
          <validation-provider
            #default="validationContext"
            name="name"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('schedule_alarm.name')"
              label-for="name"
            >
              <b-form-input
                id="name"
                v-model="itemData.name"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="tenants">
            <b-form-group
              class="text-capitalize"
              :label="$tc('tenants.title', 1)"
              label-for="tenants"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.tenants"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="tenantsOptions"
                :clearable="false"
                :reduce="(val) => val.value"
                input-id="tenants"
                multiple
              />

              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Facility categories -->
          <b-form-group
            class="text-capitalize"
            :label="$tc('facility_category.title', 2)"
            label-for="facility-categories"
          >
            <v-select
              v-model="itemData.facility_categories"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="facilityCategoryOptions"
              :reduce="(val) => val.value"
              input-id="facility-categories"
              multiple
            />
          </b-form-group>

          <!-- Units -->
          <b-form-group
            class="text-capitalize"
            :label="$tc('unit.title', 2)"
            label-for="units"
          >
            <facilities-paginated
              v-model="itemData.units"
              :multiple="true"
              :tenant-ids="itemData.tenants"
              :category-ids="itemData.facility_categories"
              :show-select-all="true"
              :is-saturday="itemData.is_saturday"
            />
          </b-form-group>

          <!-- Saturday -->
          <b-form-group
            class="text-capitalize"
            label-for="is_saturday"
          >
            <b-form-checkbox
              id="is_saturday"
              v-model="itemData.is_saturday"
            >
              {{ $t("unit.is_saturday") }}
            </b-form-checkbox>
          </b-form-group>

          <!-- Tipos de alarma -->
          <multiple-alarms-select
            :itemData="{ type_alarm: itemData.types_alarms ? itemData.types_alarms : [] }"
            :options="typesAlarmsOptions"
            @update:itemData="itemData.types_alarms = $event.type_alarm"
          />

          <!-- Allow alarms switch -->
          <b-form-group class="text-capitalize">
            <div class="d-flex align-items-center">
              <b-form-checkbox
                v-model="itemData.allow_alarms"
                switch
              />
              <label class="mb-0 ml-2">{{ $t('schedule_alarm.allow_alarms') }}</label>
            </div>
          </b-form-group>

          <!-- Holiday Switch -->
          <b-form-group class="text-capitalize">
            <div class="d-flex align-items-center">
              <b-form-checkbox
                v-model="itemData.apply_hollidays"
                switch
              />
              <label class="mb-0 ml-2">{{ $t('schedule_alarm.apply_holliday') }}</label>
            </div>
          </b-form-group>

          <!-- Normal day -->
          <b-tabs content-class="w-100">
            <b-tab
              v-for="(weekday, index) in weekdays"
              :key="index"
              :title="$t(`alarm_views.fields.periodicity.days.${weekday.text}`)"
            >
              <table class="table table-sm w-75 mb-2">
                <tr>
                  <th>{{ $t("alarm_views.fields.periodicity.from") }}</th>
                  <th>{{ $t("alarm_views.fields.periodicity.to") }}</th>
                  <th></th>
                </tr>
                <tr v-for="(dt, dt_index) in weekday.data" :key="dt_index">
                  <td>
                    <input
                      type="time"
                      :name="`time-start-${index}-${dt_index}`"
                      v-model="weekday.data[dt_index].start_at"
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      :name="`time-end-${index}-${dt_index}`"
                      v-model="weekday.data[dt_index].end_at"
                    />
                  </td>
                  <td>
                    <b-button
                      variant="danger"
                      @click="removeDt(index, dt_index)"
                      size="sm"
                    >
                      {{ $t("alarm_views.fields.periodicity.btns.delete") }}
                    </b-button>
                  </td>
                </tr>
              </table>

              <div class="d-flex justify-content-end">
                <b-button
                  variant="outline-primary"
                  class="mr-1"
                  :disabled="weekday.data.length === 0"
                  @click="replicateDaySchedule(index)"
                  size="sm"
                >
                  {{ $t("schedule_alarm.replicate_all_days") }}
                </b-button>
                <b-button variant="success" @click="addDt(index)" size="sm">
                  {{ $t("alarm_views.fields.periodicity.btns.add") }}
                </b-button>
              </div>
            </b-tab>

            <p class="text-danger" v-if="showWeekdaysValidationErrors">
              {{ $t("alarm_views.fields.periodicity.messages.invalid_schedule") }}
            </p>
          </b-tabs>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              :disabled="isSubmitting"
              @click="onSubmit"
              type="button"
            >
              <b-spinner v-if="isSubmitting" small class="mr-50" />
              {{ $t(isSubmitting ? "actions.saving" : "actions.save") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              :disabled="isSubmitting"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </validation-observer>
      </div>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BTime,
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
  BSpinner,
  BRow,
  BCol,
} from "bootstrap-vue";
import {ValidationProvider, ValidationObserver} from "vee-validate";
import vSelect from "vue-select";
import {ref, computed, reactive} from "@vue/composition-api";
import {required, alphaNum, email, positive, required_if} from "@validations";
import {useToast} from "vue-toastification/composition";
import ToastificationContent
  from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import {useUtils as useI18nUtils} from "@core/libs/i18n";
import FacilitiesPaginated from "@/components/FacilitiesPaginated.vue";
import MultipleAlarmsSelect from "@/components/Alarm/MultipleAlarmsSelect";

export default {
  components: {
    BTime,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    BFormCheckbox,
    BSpinner,
    BRow,
    BCol,
    ValidationProvider,
    ValidationObserver,
    FacilitiesPaginated,
    MultipleAlarmsSelect,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    tenantsOptions: {
      type: Array,
      required: true,
    },
    typesAlarmsOptions: {
      type: Array,
      required: true,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
    };
  },
  setup(props, {emit}) {

    const {t} = useI18nUtils();
    const showWeekdaysValidationErrors = ref(false);
    const isSubmitting = ref(false);
    const trans = {
      "messages.edit_save.successfully_created": t(
        "messages.edit_save.successfully_created"
      ),
      "messages.edit_save.updated_successfully": t(
        "messages.edit_save.updated_successfully"
      ),
    };
    const blankData = {
      tenants: [],
      types_alarms: null,
      datetimes: null,
      name: "",
      apply_hollidays: false,
      allow_alarms: false,
      units: [],
      facility_categories: [],
      isHolidayMode: false,
      is_saturday: false
    };
    const toast = useToast();
    const itemData = ref(JSON.parse(JSON.stringify(blankData)));
    store.dispatch("facility_category/getAll");
    const facilityCategoryOptions = computed(
      () => store.getters["facility_category/getSelectedItems"]
    );
    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
    };

    const formatFacilityOption = unit => {
      const source = unit && typeof unit === "object"
        ? (unit.unit || unit.unit_data || unit)
        : {};
      const id = source.id ?? source.id_unit ?? unit?.id_unit ?? unit?.value ?? unit;
      const label = unit?.label ??
        source.description ??
        source.name ??
        source.text ??
        (id != null ? `Instalación #${id}` : "");

      return {
        ...(unit && typeof unit === "object" ? unit : {}),
        ...(source && typeof source === "object" ? source : {}),
        id,
        value: id,
        label,
      };
    };

    // Holiday mode
    const isHolidayMode = ref(itemData.value.isHolidayMode);
    const holidayData = reactive({
      day: 1,
      month: 1
    });

    // Days options (1-31)
    const daysOptions = Array.from({length: 31}, (_, i) => ({
      label: String(i + 1),
      value: i + 1
    }));

    // Months options
    const monthsOptions = [
      {label: t('schedule_alarm.months.january'), value: 1},
      {label: t('schedule_alarm.months.february'), value: 2},
      {label: t('schedule_alarm.months.march'), value: 3},
      {label: t('schedule_alarm.months.april'), value: 4},
      {label: t('schedule_alarm.months.may'), value: 5},
      {label: t('schedule_alarm.months.june'), value: 6},
      {label: t('schedule_alarm.months.july'), value: 7},
      {label: t('schedule_alarm.months.august'), value: 8},
      {label: t('schedule_alarm.months.september'), value: 9},
      {label: t('schedule_alarm.months.october'), value: 10},
      {label: t('schedule_alarm.months.november'), value: 11},
      {label: t('schedule_alarm.months.december'), value: 12}
    ];

    const onSubmit = async () => {
      if (isSubmitting.value) return;

      let dispatch;

      const validation = await refFormObserver.value.validate();

      const weekdaysValidation = isHolidayMode.value ? true : validateDateTimes();

      showWeekdaysValidationErrors.value = !weekdaysValidation;

      if (validation && weekdaysValidation) {
        isSubmitting.value = true;

        let datetimesData;

        if (isHolidayMode.value) {
          datetimesData = {
            day: holidayData.day,
            month: holidayData.month,
            year: null
          };
        } else {
          // Normal mode: prepare weekdays data
          datetimesData = Object.values(weekdays)
            .filter(wkd => (wkd.data[0] ? {...wkd.data[0]} : false))
            .map(wkd => (wkd.data.map(_wkd => ({..._wkd}))))
            .flat();
        }

        const data = {
          name: itemData.value.name,
          tenants: itemData.value.tenants,
          types_alarms: itemData.value.types_alarms,
          apply_hollidays: itemData.value.apply_hollidays,
          allow_alarms: itemData.value.allow_alarms,
          units: (itemData.value.units || []).map(unit => (
            typeof unit === "object"
              ? (unit.id ?? unit.id_unit ?? unit.value)
              : unit
          )),
          datetimes: isHolidayMode.value ? [] : datetimesData,
        };

        if (props.isAdd) {
          dispatch = store.dispatch("schedule_alarm/add", data);
        } else {
          const req = {
            id: itemData.value.id,
            params: data,
          };
          dispatch = store.dispatch("schedule_alarm/edit", req);
        }
        dispatch
          .then((response) => {
            if (response.success) {
              toast({
                component: ToastificationContent,
                props: {
                  title: response.message,
                  icon: "CheckIcon",
                  variant: "success",
                },
              });
              emit("refetch-data");
              emit("update:is-sidebar-active", false);
            } else {
              toast({
                component: ToastificationContent,
                props: {
                  title: response.message,
                  icon: "AlertTriangleIcon",
                  variant: "danger",
                },
              });
            }
          })
          .catch((response) => {
            toast({
              component: ToastificationContent,
              props: {
                title: response.response.data.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          })
          .finally(() => {
            isSubmitting.value = false;
          });
      }
    };

    const addDt = (weekday) => {
      weekdays[weekday].data.push({
        weekday,
        start_at: null,
        end_at: null,
      });
    };

    const removeDt = (weekday, index) => {
      weekdays[weekday].data.splice(index, 1);
    };

    const replicateDaySchedule = (sourceWeekday) => {
      const sourceData = weekdays[sourceWeekday].data;

      Object.keys(weekdays).forEach(targetWeekday => {
        if (Number(targetWeekday) === Number(sourceWeekday)) return;

        weekdays[targetWeekday].data = sourceData.map(datetime => ({
          ...datetime,
          weekday: Number(targetWeekday),
        }));
      });
    };

    const {refFormObserver, getValidationState, resetForm} = formValidation(resetData);
    const loadData = () => {
      for (let index = 0; index < 7; index++) {
        weekdays[index].data = []
      }
      if (!props.isAdd) {
        const editData = JSON.parse(JSON.stringify(props.itemEdit));
        itemData.value = {
          ...editData,
          facility_categories: [],
          tenants: props.itemEdit.tenants.map(tenant => tenant.id),
          types_alarms: props.itemEdit.types_alarms.map(typeAlarm => typeAlarm.id),
          units: (props.itemEdit.units || []).map(formatFacilityOption),
          allow_alarms: Boolean(props.itemEdit.allow_alarms),
          apply_hollidays: Boolean(
            props.itemEdit.apply_hollidays ?? props.itemEdit.apply_holliday
          ),
        };
        (itemData.value.datetimes || []).forEach(wkd => weekdays[wkd.weekday].data.push(wkd));
      }
    };

    const weekdays = reactive({
      0: {text: "sun", data: []},
      1: {text: "mon", data: []},
      2: {text: "tue", data: []},
      3: {text: "wed", data: []},
      4: {text: "thu", data: []},
      5: {text: "fri", data: []},
      6: {text: "sat", data: []},
    });

    const validateDateTimes = () => {
      let valid = false;

      for (const key in weekdays) {
        const wkday = weekdays[key];
        if (wkday.data.length > 0) {
          const invalid = wkday.data.filter(item => {
            const start = !item.start_at ? -1 : parseInt(item.start_at.replace(':', ''));
            const end = !item.end_at ? -1 : parseInt(item.end_at.replace(':', ''));

            return start === -1 || end === -1;
          });
          valid = invalid.length === 0;
        }
      }

      return valid;
    }

    return {
      itemData,
      onSubmit,
      refFormObserver,
      getValidationState,
      loadData,
      resetForm,
      weekdays,
      addDt,
      removeDt,
      replicateDaySchedule,
      showWeekdaysValidationErrors,
      isHolidayMode,
      holidayData,
      daysOptions,
      monthsOptions,
      facilityCategoryOptions,
      isSubmitting,
    };
  },
};
</script>
